import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Empty, Heading, ITimeMachine, Skeleton, Table } from '@treams/treams-ui';
import { ReactNode, useState } from 'react';
import { get, chunk, orderBy } from 'lodash';
import moment from 'moment';
import { useEffect } from 'react';
import { Box } from 'reflexbox';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import { displayFloat } from '../metrics/AnalyticsNumberMetric';
import AnalyticsMetricDetailsTabs from './AnalyticsMetricDetailsTabs';
import AnalyticsMetricDetailsExport from './AnalyticsMetricDetailsExport';
import { useRouteMatch } from '@treams/treams-routes';
import { ActivityIcon } from '@treams/treams-icons';

function Pagination<T>(props: {
	rows: T[];
	renderRows: (row: T[]) => ReactNode;
	renderNavigation: (navigation: {
		pagination: {
			max: number;
			currentPage: number;
			maxPages: number;
			perPage: number;
		};
		disabled: {
			next: boolean;
			previous: boolean;
		};
		next: () => void;
		previous: () => void;
	}) => ReactNode;
}) {
	const { rows } = props;
	const perPage = 10;
	const maxPages = Math.ceil(rows.length / perPage);
	const [currentPage, setCurrentPage] = useState(1);
	const chunks = chunk(rows, perPage);

	return (
		<>
			{props.renderRows(chunks[currentPage - 1])}

			{props.renderNavigation({
				pagination: {
					max: rows.length,
					maxPages,
					currentPage,
					perPage,
				},
				disabled: {
					previous: currentPage < 2,
					next: currentPage + 1 > maxPages,
				},
				previous: () => {
					setCurrentPage(currentPage - 1);
				},
				next: () => {
					setCurrentPage(currentPage + 1);
				},
			})}
		</>
	);
}

type TableCellValue = {
	rawValue?: string | number | Date;
	renderValue: ReactNode;
	excel?: {
		type?: StringConstructor | NumberConstructor | DateConstructor;
		format?: string;
		value?: { value: number; total: number } | string | number | Date;
	};
};

const tableCellOverrides: Record<
	string,
	(row: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>) => TableCellValue
> = {
	'retention.contractLength': (row) => {
		if (
			row.startDate?.__typename !== 'AnalyticsDetailDate' ||
			row.endDate?.__typename !== 'AnalyticsDetailDate'
		) {
			return { renderValue: <>&mdash;</> };
		}

		const startDate = moment(row.startDate.date);
		const endDate = moment(row.endDate.date);

		const diffInMs = endDate.diff(startDate);

		const rawValue = diffInMs;

		const diffInMonths = endDate.diff(startDate, 'months');

		if (diffInMonths === 0) {
			return {
				rawValue,
				renderValue: moment.duration(diffInMs).humanize(),
			};
		}

		const diffInYears = endDate.diff(startDate, 'years');

		if (diffInYears === 0) {
			return {
				rawValue,
				renderValue: moment.duration(diffInMonths - diffInYears * 12, 'months').humanize(),
			};
		}

		const remainderInMonths = diffInMonths - diffInYears * 12;

		return {
			rawValue,
			renderValue: [
				moment.duration(diffInYears, 'years').humanize(),
				...(remainderInMonths > 0
					? [moment.duration(diffInMonths - diffInYears * 12, 'months').humanize()]
					: []),
			].join(', '),
		};
	},
};

export function tableCellValue(
	localize: (item: graphql.LocalizedString) => { languageID: string; text: string },
	row: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>,
	header: graphql.AnalyticsDetailHeader,
	sortOrder?: 'asc' | 'desc',
): TableCellValue {
	const value = get(row, header.value);

	if (header.type === 'list') {
		const list = row[header.key] as graphql.AnalyticsDetailList;

		if (!list || list.__typename !== 'AnalyticsDetailList' || list.items.length === 0) {
			return {
				renderValue: <>&mdash;</>,
			};
		}

		const values = list.items.map((item) => {
			const newRow =
				header.key === 'departments'
					? { department: item }
					: header.key === 'managers'
					? { user: item }
					: {};

			return tableCellValue(localize, newRow, { ...header, type: undefined });
		});

		const ordered = orderBy(values ?? [], (item) => item.rawValue, sortOrder);

		return {
			rawValue: ordered[0].rawValue,
			renderValue: ordered.map((item) => item.renderValue).join(', '),
		};
	}

	if (tableCellOverrides[header.value]) {
		return tableCellOverrides[header.value](row);
	}

	if (value?.__typename === 'AnalyticsDetailInt') {
		return {
			rawValue: value.integer,
			renderValue: value.integer,
			excel: {
				type: Number,
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailFloat') {
		return {
			rawValue: value.float,
			renderValue: value.float,
			excel: {
				type: Number,
				format: '0.00',
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailPercentage') {
		return {
			rawValue: value.percentage,
			renderValue: `${displayFloat(value.percentage * 100)}%`,
			excel: {
				type: Number,
				format: '0.0%',
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailFraction') {
		return {
			rawValue: value.value === 0 && value.total === 0 ? undefined : value.value / value.total,
			renderValue:
				value.value === 0 && value.total === 0 ? <>&mdash;</> : `${value.value} / ${value.total}`,
			excel: {
				value: { value: value.value, total: value.total },
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailText') {
		return {
			rawValue: value.text.toLowerCase(),
			renderValue: value.text,
			excel: {
				type: String,
				value: value.text,
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailDate') {
		const date = moment(value.date);

		return {
			rawValue: date.toDate(),
			renderValue: date.format('LL'),
			excel: {
				type: Date,
				format: 'd mmmm yyyy',
			},
		};
	}

	if (header.type === 'date' && value) {
		const date = moment(value as unknown as string);

		return {
			rawValue: date.toDate(),
			renderValue: date.format('LL'),
			excel: {
				type: Date,
				format: 'd mmmm yyyy',
			},
		};
	}

	if (value && typeof value !== 'object') {
		return {
			rawValue: value,
			renderValue: value,
		};
	}

	if (value && Array.isArray(value) && 'languageID' in value[0]) {
		const text = localize(value).text;
		return {
			rawValue: text.toLowerCase(),
			renderValue: text,
			excel: {
				type: String,
				value: text,
			},
		};
	}

	return {
		renderValue: <>&mdash;</>,
		excel: {
			type: String,
		},
	};
}

export function TableHeader(props: {
	headers: graphql.AnalyticsDetailHeader[];
	sortedBy: number;
	sortOrder: 'asc' | 'desc' | undefined;
	setSortOrder: (order: 'asc' | 'desc') => void;
	setSortedBy: (index: number) => void;
}) {
	const { t } = useTranslation();

	return (
		<Table.Header>
			{props.headers.map((header, index) => (
				<Table.Cell
					basis={1}
					key={header.key}
					sorted={props.sortedBy === index ? props.sortOrder : undefined}
					onClick={() => {
						if (props.sortedBy === index) {
							props.setSortOrder(props.sortOrder === 'asc' ? 'desc' : 'asc');
							return;
						}
						props.setSortedBy(index);
						props.setSortOrder('asc');
					}}
				>
					{t(`analytics.metric.details.${header.key}`)}
				</Table.Cell>
			))}
		</Table.Header>
	);
}

export default function AnalyticsMetricDetails(props: {
	ids: string[];
	type: graphql.AnalyticsType;
	filters: graphql.AnalyticsFiltersV2;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const match = useRouteMatch<{ id: string }>('/analytics/v2/metric/:groupID/:metricID/:id')?.params
		.id;

	const id = props.ids.find((id) => _.kebabCase(id) === match) ?? props.ids[0];

	const query = graphql.useAnalyticsMetricDetailsQuery({
		variables: { id: id, filters: props.filters, type: props.type },
	});

	const [sortedBy, setSortedBy] = useState(0);
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

	useEffect(() => {
		setSortedBy(0);
		setSortOrder('asc');
	}, [id]);

	const sortHeader = query.data?.analytics.detail?.headers[sortedBy];
	const rows: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>[] =
		(query.data?.analytics.detail?.rows as graphql.AnalyticsDetailRow[])?.map((row) => {
			return Object.fromEntries(row.columns.map((column) => [column.key, column.value]));
		}) ?? [];

	const sortedRows = orderBy(
		rows,
		(row) => tableCellValue(localize, row, sortHeader!, sortOrder).rawValue,
		sortOrder,
	);

	const detail = query.data?.analytics.detail;

	return (
		<>
			<Heading mt={4}>{t('analytics.metric.detailsTitle')}</Heading>
			<Box mb={3}>
				<AnalyticsMetricDetailsTabs
					ids={props.ids}
					type={props.type}
					filters={props.filters}
					timeMachine={props.timeMachine}
					downloadButton={
						query.data && detail ? (
							<AnalyticsMetricDetailsExport
								detail={detail as graphql.AnalyticsDetail}
								rows={rows}
								filters={props.filters}
								timeMachine={props.timeMachine}
							/>
						) : (
							<Button.Skeleton small />
						)
					}
				/>
			</Box>
			{!query.loading ? (
				query.data &&
				detail &&
				(rows.length > 0 ? (
					<Pagination
						rows={sortedRows}
						renderRows={(rows) => (
							<Table>
								<TableHeader
									headers={detail.headers}
									sortOrder={sortOrder}
									setSortOrder={setSortOrder}
									sortedBy={sortedBy}
									setSortedBy={setSortedBy}
								/>
								{rows.map((row, index) => (
									<Table.Row key={index}>
										{detail.headers.map((header) => (
											<Table.Cell basis={1} key={`${header.key}-${index}`}>
												{header.key === 'user' && (
													<Avatar mr={2} user={row.user as graphql.User} size={20} flexShrink={0} />
												)}
												{tableCellValue(localize, row, header).renderValue}
											</Table.Cell>
										))}
									</Table.Row>
								))}
							</Table>
						)}
						renderNavigation={({ pagination, disabled, next, previous }) => (
							<Table.Pagination
								pagination={pagination}
								disabled={disabled}
								onNext={next}
								onPrevious={previous}
							/>
						)}
					/>
				) : (
					<Table>
						<TableHeader
							headers={detail.headers}
							sortOrder={sortOrder}
							setSortOrder={setSortOrder}
							sortedBy={sortedBy}
							setSortedBy={setSortedBy}
						/>
						<Skeleton.Empty
							contents={
								<Empty
									icon={<ActivityIcon />}
									title={t('analytics.metric.empty.title')}
									description={t('analytics.metric.empty.description')}
								/>
							}
						>
							<Skeleton.Repeat count={5}>
								<Table.Row>
									<Skeleton.Repeat count={detail.headers.length}>
										<Table.Cell.Skeleton basis={1} />
									</Skeleton.Repeat>
								</Table.Row>
							</Skeleton.Repeat>
						</Skeleton.Empty>
					</Table>
				))
			) : (
				<Table>
					<Table.Header>
						<Table.Cell.Skeleton basis={0.33} />
						<Table.Cell.Skeleton basis={0.33} />
						<Table.Cell.Skeleton basis={0.33} />
					</Table.Header>
					<Skeleton.Repeat count={3}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.33} />
							<Table.Cell.Skeleton basis={0.33} />
							<Table.Cell.Skeleton basis={0.33} />
						</Table.Row>
					</Skeleton.Repeat>
				</Table>
			)}
		</>
	);
}
